import { COMPANY_SOCIAL_PLATFORMS } from '@tabbio/types';
import type {
  PublicProfileBlock as SharedPublicProfileBlock,
  PublicProfileBlockType as SharedPublicProfileBlockType,
  PublicProfileContact as SharedPublicProfileContact,
  PublicProfileContactChannelId as SharedPublicProfileContactChannelId,
  PublicProfileMode,
  PublicProfileOpenRolesLayout as SharedPublicProfileOpenRolesLayout,
  PublicProfileSocial as SharedPublicProfileSocial,
  PublicProfileSocialId as SharedPublicProfileSocialId,
} from '@tabbio/types';

export type PublicProfileSocialId = SharedPublicProfileSocialId;

export type PublicProfileSocialLink = SharedPublicProfileSocial;

export type PublicProfileContactChannelId = SharedPublicProfileContactChannelId;

export type PublicProfileContactChannel = SharedPublicProfileContact;

export type PublicProfileBlockType = SharedPublicProfileBlockType;

export type PublicProfileOpenRoleRemote = 'on-site' | 'hybrid' | 'remote';
export type PublicProfileOpenRolesLayout = SharedPublicProfileOpenRolesLayout;

export interface PublicProfileOpenRole {
  id: string;
  title: string;
  department: string;
  location: string;
  remote: PublicProfileOpenRoleRemote;
  type: string;
  applications: number;
  enabled: boolean;
}

export interface PublicProfileBlock extends SharedPublicProfileBlock {
  roles?: PublicProfileOpenRole[];
  rolesLayout?: PublicProfileOpenRolesLayout;
  talentPoolEnabled?: boolean;
  thumbnailUrl?: string | null;
}

export type PublicProfileButtonStyle = 'fill' | 'outline' | 'soft' | 'glass';
export type PublicProfileButtonShape = 'square' | 'rounded' | 'pill';

export interface PublicProfileTheme {
  id: 'tabbio' | 'air' | 'midnight' | 'galaxy' | 'glass' | 'aurora' | 'mono' | 'sunset' | 'ocean';
  labelKey: string;
  previewBg: string;
  previewCard: string;
  previewText: string;
  defaultAccent: string;
  defaultButtonStyle: PublicProfileButtonStyle;
  defaultButtonShape: PublicProfileButtonShape;
}

export type PublicProfileSharePreviewBackground = 'gradient' | 'light' | 'dark';

export interface PublicProfileShareBackgroundOption {
  id: PublicProfileSharePreviewBackground;
  labelKey: string;
  start: string;
  end: string;
}

export type PublicProfileChecklistId =
  | 'name_bio'
  | 'profile_image'
  | 'socials'
  | 'important_links'
  | 'customize_design'
  | 'share_tabbio';

// Company setup checklist (PRD §11), Phase 1: the design step was removed
// with the Design module (companies ship the clean default style), leaving
// five persisted, backend-derived outcomes.
export type PublicProfileCompanyChecklistId =
  | 'company_identity'
  | 'logo'
  | 'social_or_website'
  | 'careers_ready'
  | 'shared';

export type PublicProfileAnyChecklistId =
  | PublicProfileChecklistId
  | PublicProfileCompanyChecklistId;

/**
 * Server checklist object on the company builder GET / share-confirm payloads.
 * Additive to the shared PublicProfileBuilderState contract — read tolerantly:
 * absent means "no persisted progress known", never "complete".
 */
export interface PublicProfileCompanyChecklist {
  companyIdentity: boolean;
  logo: boolean;
  socialOrWebsite: boolean;
  careersReady: boolean;
  shared: boolean;
}

export interface PublicProfileExploreTile {
  id: string;
  labelKey: string;
  color: string;
  types: PublicProfileBlockType[];
}

export interface PublicProfileSuggestedBlock {
  type: PublicProfileBlockType;
  titleKey: string;
  descKey: string;
}

export interface PublicProfileQuickAddItem {
  type: PublicProfileBlockType;
  labelKey: string;
  iconKey: 'collection' | 'link' | 'header' | 'form';
}

export type PublicProfileExploreGroupId =
  | 'profile'
  | 'video'
  | 'document'
  | 'audio'
  | 'contact'
  | 'commerce'
  | 'text'
  | 'hiring'
  | 'other';

export interface PublicProfileQrActionCapabilities {
  canSave: boolean;
  canDownload: boolean;
}

export const PUBLIC_PROFILE_SUPPORTED_BLOCKS_INDIVIDUAL: PublicProfileBlockType[] = [
  'webcv',
  'career',
  'website',
  'portfolio',
  'services',
  'visit',
  'map',
  'header',
  'text',
  'form',
  'image',
  'video',
  'music',
  'pdf',
  'booking',
  'digitalproduct',
  'insights',
  'faqs',
  'testimonial',
];

// Phase 1 P0 company catalogue (PRD §6.4): Careers (openroles), Where we work
// (map), Text, Candidate FAQ. This gates ADDING blocks only — legacy blocks
// already stored on a page keep rendering and stay editable.
//
// No 'website': a company's site is one URL, which belongs in the hero link row
// beside the socials, not in a section of its own that can hold nothing else.
export const PUBLIC_PROFILE_SUPPORTED_BLOCKS_COMPANY: PublicProfileBlockType[] = [
  'openroles',
  'map',
  'text',
  'links',
  'faqs',
];

// Company "Add a block" catalogue (PRD §6.5): only the non-unique basics are
// offered. Unique/seeded blocks (openroles, map, faqs) already live on the
// page — toggles re-enable them, and smart add updates/creates the map — so
// they never appear here. This intentionally does NOT narrow
// PUBLIC_PROFILE_SUPPORTED_BLOCKS_COMPANY, which gates rendering/editing of
// existing blocks.
export const PUBLIC_PROFILE_ADD_BASICS_COMPANY: PublicProfileBlockType[] = [
  'links',
  'text',
];

// Company blocks the employer cannot delete — 'map' is seeded onto every
// company page and absent from the add catalogue above, so a delete would be
// a one-way door. Enabled/disabled is the only lever.
export const PUBLIC_PROFILE_PERMANENT_BLOCKS_COMPANY: PublicProfileBlockType[] = [
  'map',
];

export const PUBLIC_PROFILE_AUTHORED_BLOCK_TYPES: PublicProfileBlockType[] = [
  'header',
  'text',
  'links',
  'form',
  'insights',
  'faqs',
  'testimonial',
  'booking',
  'talentpool',
  'image',
  'pdf',
  'video',
  'music',
  'digitalproduct',
];

export const PUBLIC_PROFILE_DATA_BACKED_BLOCK_TYPES: PublicProfileBlockType[] = [
  'webcv',
  'career',
  'portfolio',
  'services',
  'openroles',
  'team',
];

export const PUBLIC_PROFILE_SOCIAL_ORDER: PublicProfileSocialId[] = [
  'linkedin',
  'instagram',
  'x',
  'tiktok',
  'youtube',
  'snapchat',
  'facebook',
  'discord',
  'pinterest',
  'spotify',
  'tabbio',
  'whatsapp',
  'email',
];

// Phase 1 company social platforms (PRD §6.2): website (a page field) plus
// this fixed list. Derived from the shared COMPANY_SOCIAL_PLATFORMS contract
// so the builder can never drift from the server's allowed set.
export const PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY: PublicProfileSocialId[] = [
  ...COMPANY_SOCIAL_PLATFORMS,
];

export function getPublicProfileSocialOrder(
  mode: 'individual' | 'company',
): PublicProfileSocialId[] {
  return mode === 'company'
    ? PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY
    : PUBLIC_PROFILE_SOCIAL_ORDER;
}

export const PUBLIC_PROFILE_CONTACT_ORDER: PublicProfileContactChannelId[] = [
  'tabbioMessage',
  'whatsapp',
  'telegram',
  'slack',
  'email',
  'phone',
  'landline',
];

/**
 * Channels that ship with the page rather than being added to it.
 *
 * Tabbio Message is first-party: it is switched off, never taken away, and it
 * carries no address for the owner to type — reaching them through Tabbio needs
 * only their page. So its row shows Enable/Disable instead of Add/Remove, skips
 * the value input, and keeps whatever it holds when switched off, where a
 * removable channel is cleared.
 */
export function isBuiltInContactChannel(
  id: PublicProfileContactChannelId,
): boolean {
  return id === 'tabbioMessage';
}

export const PUBLIC_PROFILE_TOP_SOCIALS: PublicProfileSocialId[] = [
  'linkedin',
  'instagram',
  'x',
  'tiktok',
  'youtube',
];

// The builder header and the preview both render every channel the user
// enabled, in sheet order. There is no "top contacts" subset: capping the row
// at Tabbio/WhatsApp/email made Telegram, Slack, phone and landline look like
// they had never saved, since the sheet is the only other place they appear.
export const PUBLIC_PROFILE_CONTACT_ROW_ORDER = PUBLIC_PROFILE_CONTACT_ORDER;

export const PUBLIC_PROFILE_QUICK_ADD_ITEMS: PublicProfileQuickAddItem[] = [
  { type: 'webcv', labelKey: 'publicProfile.blocks.webcv.title', iconKey: 'collection' },
  { type: 'website', labelKey: 'publicProfile.blocks.website.title', iconKey: 'link' },
  { type: 'header', labelKey: 'publicProfile.blocks.header.title', iconKey: 'header' },
  { type: 'form', labelKey: 'publicProfile.blocks.form.title', iconKey: 'form' },
];

export const PUBLIC_PROFILE_SHARE_PREVIEW_BACKGROUNDS: PublicProfileShareBackgroundOption[] = [
  {
    id: 'gradient',
    labelKey: 'publicProfile.shareSheet.customize.backgrounds.gradient',
    start: '#5A2AFF',
    end: '#3B82F6',
  },
  {
    id: 'light',
    labelKey: 'publicProfile.shareSheet.customize.backgrounds.light',
    start: '#F8FAFC',
    end: '#E2E8F0',
  },
  {
    id: 'dark',
    labelKey: 'publicProfile.shareSheet.customize.backgrounds.dark',
    start: '#111827',
    end: '#0B1220',
  },
];

export const DEFAULT_PUBLIC_PROFILE_OPEN_ROLES: PublicProfileOpenRole[] = [
  {
    id: 'r1',
    title: 'Senior Frontend Engineer',
    department: 'Engineering',
    location: 'Dubai, UAE',
    remote: 'hybrid',
    type: 'Full-time',
    applications: 0,
    enabled: true,
  },
  {
    id: 'r2',
    title: 'Product Designer',
    department: 'Design',
    location: 'Dubai, UAE',
    remote: 'on-site',
    type: 'Full-time',
    applications: 1,
    enabled: true,
  },
  {
    id: 'r3',
    title: 'Growth Marketing Manager',
    department: 'Marketing',
    location: 'Riyadh, KSA',
    remote: 'remote',
    type: 'Full-time',
    applications: 0,
    enabled: true,
  },
  {
    id: 'r4',
    title: 'Sales Development Rep',
    department: 'Sales',
    location: 'Abu Dhabi, UAE',
    remote: 'on-site',
    type: 'Full-time',
    applications: 0,
    enabled: true,
  },
  {
    id: 'r5',
    title: 'DevOps Engineer',
    department: 'Engineering',
    location: 'Dubai, UAE',
    remote: 'remote',
    type: 'Contract',
    applications: 0,
    enabled: true,
  },
];

export const DEFAULT_PUBLIC_PROFILE_BLOCKS_INDIVIDUAL: PublicProfileBlock[] = [
  { id: 'web_cv', type: 'webcv', enabled: true, order: 0, config: {} },
  { id: 'website', type: 'website', enabled: true, order: 1, config: {} },
  { id: 'portfolio', type: 'portfolio', enabled: true, order: 2, config: {} },
  { id: 'services', type: 'services', enabled: true, order: 3, config: {} },
  { id: 'visit_us', type: 'visit', enabled: true, order: 4, config: {} },
];

// No website block: a company's site is a hero link, not a section. Shipping
// one by default meant filling in the website grew a block nobody asked for.
export const DEFAULT_PUBLIC_PROFILE_BLOCKS_COMPANY: PublicProfileBlock[] = [
  {
    id: 'open_roles',
    type: 'openroles',
    enabled: true,
    order: 0,
    config: { rolesLayout: 'tabs', talentPoolEnabled: true },
    rolesLayout: 'tabs',
    talentPoolEnabled: true,
    roles: [],
  },
  {
    id: 'team',
    type: 'team',
    enabled: true,
    order: 1,
    config: {},
  },
  {
    id: 'visit_us',
    type: 'visit',
    enabled: true,
    order: 2,
    config: {},
  },
  {
    id: 'map',
    type: 'map',
    enabled: true,
    order: 3,
    config: {},
  },
];

export const DEFAULT_PUBLIC_PROFILE_BLOCKS = DEFAULT_PUBLIC_PROFILE_BLOCKS_INDIVIDUAL;

export const DEFAULT_PUBLIC_PROFILE_SOCIALS: PublicProfileSocialLink[] = [
  { id: 'linkedin', url: '', enabled: true, order: 0 },
  { id: 'instagram', url: '', enabled: true, order: 1 },
  { id: 'x', url: '', enabled: true, order: 2 },
  { id: 'tiktok', url: '', enabled: true, order: 3 },
  { id: 'youtube', url: '', enabled: true, order: 4 },
  { id: 'tabbio', url: '', enabled: true, order: 5 },
  { id: 'whatsapp', url: '', enabled: false, order: 6 },
  { id: 'email', url: '', enabled: false, order: 7 },
];

export const DEFAULT_PUBLIC_PROFILE_CONTACTS: PublicProfileContactChannel[] = [
  { id: 'tabbioMessage', enabled: true, value: '', order: 0 },
  { id: 'whatsapp', enabled: true, value: '', order: 1 },
  { id: 'telegram', enabled: false, value: '', order: 2 },
  { id: 'slack', enabled: false, value: '', order: 3 },
  { id: 'email', enabled: true, value: '', order: 4 },
  { id: 'phone', enabled: false, value: '', order: 5 },
  { id: 'landline', enabled: false, value: '', order: 6 },
];

export const PUBLIC_PROFILE_THEMES: PublicProfileTheme[] = [
  {
    id: 'tabbio',
    labelKey: 'publicProfile.design.themes.tabbio',
    previewBg: '#1E1E1E',
    previewCard: 'rgba(255,255,255,0.16)',
    previewText: '#FFFFFF',
    defaultAccent: '#5A2AFF',
    defaultButtonStyle: 'fill',
    defaultButtonShape: 'rounded',
  },
  {
    id: 'air',
    labelKey: 'publicProfile.design.themes.air',
    previewBg: '#F4F4F4',
    previewCard: '#FFFFFF',
    previewText: '#111827',
    defaultAccent: '#5A2AFF',
    defaultButtonStyle: 'fill',
    defaultButtonShape: 'rounded',
  },
  {
    id: 'midnight',
    labelKey: 'publicProfile.design.themes.midnight',
    previewBg: '#0E1320',
    previewCard: '#171B29',
    previewText: '#FFFFFF',
    defaultAccent: '#A855F7',
    defaultButtonStyle: 'soft',
    defaultButtonShape: 'rounded',
  },
  {
    id: 'galaxy',
    labelKey: 'publicProfile.design.themes.galaxy',
    previewBg: '#1B1033',
    previewCard: '#291B4A',
    previewText: '#F8FAFC',
    defaultAccent: '#0EA5E9',
    defaultButtonStyle: 'outline',
    defaultButtonShape: 'pill',
  },
  {
    id: 'glass',
    labelKey: 'publicProfile.design.themes.glass',
    previewBg: '#7A88A2',
    previewCard: 'rgba(255,255,255,0.28)',
    previewText: '#FFFFFF',
    defaultAccent: '#A855F7',
    defaultButtonStyle: 'glass',
    defaultButtonShape: 'pill',
  },
  {
    id: 'aurora',
    labelKey: 'publicProfile.design.themes.aurora',
    previewBg: '#1D2842',
    previewCard: '#233659',
    previewText: '#F8FAFC',
    defaultAccent: '#14B8A6',
    defaultButtonStyle: 'outline',
    defaultButtonShape: 'rounded',
  },
  {
    id: 'mono',
    labelKey: 'publicProfile.design.themes.mono',
    previewBg: '#111111',
    previewCard: '#1A1A1A',
    previewText: '#F5F5F5',
    defaultAccent: '#111111',
    defaultButtonStyle: 'outline',
    defaultButtonShape: 'square',
  },
  {
    id: 'sunset',
    labelKey: 'publicProfile.design.themes.sunset',
    previewBg: '#B74D2E',
    previewCard: '#CC6443',
    previewText: '#FFF7ED',
    defaultAccent: '#F97316',
    defaultButtonStyle: 'fill',
    defaultButtonShape: 'pill',
  },
  {
    id: 'ocean',
    labelKey: 'publicProfile.design.themes.ocean',
    previewBg: '#123A5D',
    previewCard: '#17496E',
    previewText: '#F8FAFC',
    defaultAccent: '#0EA5E9',
    defaultButtonStyle: 'soft',
    defaultButtonShape: 'rounded',
  },
];

export const PUBLIC_PROFILE_ACCENTS = [
  '#5A2AFF',
  '#22C55E',
  '#0EA5E9',
  '#F97316',
  '#EF4444',
  '#111111',
  '#A855F7',
  '#14B8A6',
  '#F59E0B',
] as const;

export const PUBLIC_PROFILE_CHECKLIST_ORDER: PublicProfileChecklistId[] = [
  'name_bio',
  'profile_image',
  'socials',
  'important_links',
  'customize_design',
  'share_tabbio',
];

export const PUBLIC_PROFILE_CHECKLIST_ORDER_COMPANY: PublicProfileCompanyChecklistId[] = [
  'company_identity',
  'logo',
  'social_or_website',
  'careers_ready',
  'shared',
];

/** Maps the server checklist booleans onto ordered client checklist ids. */
export function mapCompanyChecklistToItems(
  checklist: PublicProfileCompanyChecklist | null | undefined,
): { id: PublicProfileCompanyChecklistId; completed: boolean }[] {
  const byId: Record<PublicProfileCompanyChecklistId, boolean> = {
    company_identity: checklist?.companyIdentity ?? false,
    logo: checklist?.logo ?? false,
    social_or_website: checklist?.socialOrWebsite ?? false,
    careers_ready: checklist?.careersReady ?? false,
    shared: checklist?.shared ?? false,
  };

  return PUBLIC_PROFILE_CHECKLIST_ORDER_COMPANY.map((id) => ({
    id,
    completed: byId[id],
  }));
}

export const PUBLIC_PROFILE_BLOCK_META: Record<
  PublicProfileBlockType,
  {
    titleKey: string;
    subtitleKey: string;
    color: string;
    descKey: string;
  }
> = {
  webcv: {
    titleKey: 'publicProfile.blocks.webcv.title',
    subtitleKey: 'publicProfile.blocks.webcv.subtitle',
    color: '#6366F1',
    descKey: 'publicProfile.blocks.webcv.desc',
  },
  career: {
    titleKey: 'publicProfile.blocks.career.title',
    subtitleKey: 'publicProfile.blocks.career.subtitle',
    color: '#8B5CF6',
    descKey: 'publicProfile.blocks.career.desc',
  },
  services: {
    titleKey: 'publicProfile.blocks.services.title',
    subtitleKey: 'publicProfile.blocks.services.subtitle',
    color: '#10B981',
    descKey: 'publicProfile.blocks.services.desc',
  },
  website: {
    titleKey: 'publicProfile.blocks.website.title',
    subtitleKey: 'publicProfile.blocks.website.subtitle',
    color: '#334155',
    descKey: 'publicProfile.blocks.website.desc',
  },
  insights: {
    titleKey: 'publicProfile.blocks.insights.title',
    subtitleKey: 'publicProfile.blocks.insights.subtitle',
    color: '#0EA5E9',
    descKey: 'publicProfile.blocks.insights.desc',
  },
  links: {
    titleKey: 'publicProfile.blocks.links.title',
    subtitleKey: 'publicProfile.blocks.links.subtitle',
    color: '#0EA5E9',
    descKey: 'publicProfile.blocks.links.desc',
  },
  faqs: {
    titleKey: 'publicProfile.blocks.faqs.title',
    subtitleKey: 'publicProfile.blocks.faqs.subtitle',
    color: '#64748B',
    descKey: 'publicProfile.blocks.faqs.desc',
  },
  visit: {
    titleKey: 'publicProfile.blocks.visit.title',
    subtitleKey: 'publicProfile.blocks.visit.subtitle',
    color: '#F59E0B',
    descKey: 'publicProfile.blocks.visit.desc',
  },
  openroles: {
    titleKey: 'publicProfile.blocks.openroles.title',
    subtitleKey: 'publicProfile.blocks.openroles.subtitle',
    color: '#7C3AED',
    descKey: 'publicProfile.blocks.openroles.desc',
  },
  team: {
    titleKey: 'publicProfile.blocks.team.title',
    subtitleKey: 'publicProfile.blocks.team.subtitle',
    color: '#3B82F6',
    descKey: 'publicProfile.blocks.team.desc',
  },
  talentpool: {
    titleKey: 'publicProfile.blocks.talentpool.title',
    subtitleKey: 'publicProfile.blocks.talentpool.subtitle',
    color: '#22C55E',
    descKey: 'publicProfile.blocks.talentpool.desc',
  },
  portfolio: {
    titleKey: 'publicProfile.blocks.portfolio.title',
    subtitleKey: 'publicProfile.blocks.portfolio.subtitle',
    color: '#8B5CF6',
    descKey: 'publicProfile.blocks.portfolio.desc',
  },
  testimonial: {
    titleKey: 'publicProfile.blocks.testimonial.title',
    subtitleKey: 'publicProfile.blocks.testimonial.subtitle',
    color: '#D97706',
    descKey: 'publicProfile.blocks.testimonial.desc',
  },
  booking: {
    titleKey: 'publicProfile.blocks.booking.title',
    subtitleKey: 'publicProfile.blocks.booking.subtitle',
    color: '#F59E0B',
    descKey: 'publicProfile.blocks.booking.desc',
  },
  map: {
    titleKey: 'publicProfile.blocks.map.title',
    subtitleKey: 'publicProfile.blocks.map.subtitle',
    color: '#A855F7',
    descKey: 'publicProfile.blocks.map.desc',
  },
  pdf: {
    titleKey: 'publicProfile.blocks.pdf.title',
    subtitleKey: 'publicProfile.blocks.pdf.subtitle',
    color: '#F43F5E',
    descKey: 'publicProfile.blocks.pdf.desc',
  },
  image: {
    titleKey: 'publicProfile.blocks.image.title',
    subtitleKey: 'publicProfile.blocks.image.subtitle',
    color: '#14B8A6',
    descKey: 'publicProfile.blocks.image.desc',
  },
  video: {
    titleKey: 'publicProfile.blocks.video.title',
    subtitleKey: 'publicProfile.blocks.video.subtitle',
    color: '#3B82F6',
    descKey: 'publicProfile.blocks.video.desc',
  },
  digitalproduct: {
    titleKey: 'publicProfile.blocks.digitalproduct.title',
    subtitleKey: 'publicProfile.blocks.digitalproduct.subtitle',
    color: '#EA580C',
    descKey: 'publicProfile.blocks.digitalproduct.desc',
  },
  text: {
    titleKey: 'publicProfile.blocks.text.title',
    subtitleKey: 'publicProfile.blocks.text.subtitle',
    color: '#65A30D',
    descKey: 'publicProfile.blocks.text.desc',
  },
  header: {
    titleKey: 'publicProfile.blocks.header.title',
    subtitleKey: 'publicProfile.blocks.header.subtitle',
    color: '#65A30D',
    descKey: 'publicProfile.blocks.header.desc',
  },
  form: {
    titleKey: 'publicProfile.blocks.form.title',
    subtitleKey: 'publicProfile.blocks.form.subtitle',
    color: '#16A34A',
    descKey: 'publicProfile.blocks.form.desc',
  },
  music: {
    titleKey: 'publicProfile.blocks.music.title',
    subtitleKey: 'publicProfile.blocks.music.subtitle',
    color: '#F97316',
    descKey: 'publicProfile.blocks.music.desc',
  },
};

export const PUBLIC_PROFILE_SUGGESTED_BLOCKS_INDIVIDUAL: PublicProfileSuggestedBlock[] = [
  { type: 'webcv', titleKey: 'publicProfile.blocks.webcv.title', descKey: 'publicProfile.blocks.webcv.desc' },
  { type: 'portfolio', titleKey: 'publicProfile.blocks.portfolio.title', descKey: 'publicProfile.blocks.portfolio.desc' },
  { type: 'services', titleKey: 'publicProfile.blocks.services.title', descKey: 'publicProfile.blocks.services.desc' },
  { type: 'website', titleKey: 'publicProfile.blocks.website.title', descKey: 'publicProfile.blocks.website.desc' },
  { type: 'visit', titleKey: 'publicProfile.blocks.visit.title', descKey: 'publicProfile.blocks.visit.desc' },
  { type: 'map', titleKey: 'publicProfile.blocks.map.title', descKey: 'publicProfile.blocks.map.desc' },
  { type: 'text', titleKey: 'publicProfile.blocks.text.title', descKey: 'publicProfile.blocks.text.desc' },
  { type: 'header', titleKey: 'publicProfile.blocks.header.title', descKey: 'publicProfile.blocks.header.desc' },
  { type: 'form', titleKey: 'publicProfile.blocks.form.title', descKey: 'publicProfile.blocks.form.desc' },
];

export const PUBLIC_PROFILE_EXPLORE_TILES_INDIVIDUAL: PublicProfileExploreTile[] = [
  { id: 'professional', labelKey: 'publicProfile.explore.professional', color: '#6366F1', types: ['webcv', 'portfolio', 'services', 'website'] },
  { id: 'contact', labelKey: 'publicProfile.explore.contact', color: '#14B8A6', types: ['form', 'map', 'visit'] },
  { id: 'text', labelKey: 'publicProfile.explore.text', color: '#65A30D', types: ['text', 'header'] },
  {
    id: 'all',
    labelKey: 'publicProfile.explore.all',
    color: '#374151',
    types: ['webcv', 'website', 'portfolio', 'services', 'visit', 'map', 'text', 'header', 'form'],
  },
];

export const PUBLIC_PROFILE_EXPLORE_GROUP_ORDER: PublicProfileExploreGroupId[] = [
  'profile',
  'hiring',
  'commerce',
  'video',
  'document',
  'audio',
  'contact',
  'text',
  'other',
];

export const PUBLIC_PROFILE_EXPLORE_GROUP_BY_TYPE: Record<
  PublicProfileBlockType,
  PublicProfileExploreGroupId
> = {
  webcv: 'profile',
  career: 'profile',
  services: 'commerce',
  website: 'profile',
  insights: 'profile',
  faqs: 'contact',
  links: 'profile',
  visit: 'contact',
  openroles: 'hiring',
  team: 'hiring',
  talentpool: 'hiring',
  portfolio: 'profile',
  testimonial: 'profile',
  booking: 'commerce',
  map: 'contact',
  pdf: 'document',
  image: 'video',
  video: 'video',
  digitalproduct: 'commerce',
  text: 'text',
  header: 'text',
  form: 'contact',
  music: 'audio',
};

export function createNewBlock(type: PublicProfileBlockType): PublicProfileBlock {
  if (type === 'openroles') {
    return {
      id: `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      type,
      enabled: true,
      order: 0,
      config: { rolesLayout: 'tabs', talentPoolEnabled: true },
      rolesLayout: 'tabs',
      talentPoolEnabled: true,
      roles: [],
    };
  }

  return {
    id: `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    type,
    enabled: true,
    order: 0,
    config: {},
  };
}

/**
 * The blocks the editor should list for a page of this kind.
 *
 * A company's website is a hero link, not a section: `buildPublicProfileRenderBlocks`
 * no longer mirrors `owner.website` into a company website block, so a block with
 * an empty `config.url` can never appear on the page. Pages created while the
 * block shipped by default still carry that empty block in storage, and listing
 * a block that can never render is worse than hiding it — so the editor drops it.
 * The stored block is left untouched: nothing is deleted on the owner's behalf.
 *
 * A company website block that carries its own `config.url` is a different
 * thing entirely: the renderer still builds it and `getPublishedPublicProfileBlocks`
 * still keeps it (`block.type === 'website'` → `Boolean(block.href)`), so it is
 * live on the public page. Legacy pages have these — `smart-add` used to turn any
 * pasted HTTPS URL into `{ type: 'website', config: { url } }`. Filtering those
 * out left a published section the owner could not retitle, hide, or delete, so
 * only the never-renderable ones are hidden.
 */
export function getEditableProfileBlocks<
  T extends { type: PublicProfileBlockType; config?: { url?: string | null } | null },
>(blocks: T[], mode: 'individual' | 'company'): T[] {
  if (mode !== 'company') return blocks;
  return blocks.filter(
    (block) => block.type !== 'website' || hasPublishableWebsiteUrl(block.config?.url),
  );
}

/**
 * Matches the renderer exactly: `buildPublicProfileRenderBlocks` runs
 * `getPublicProfileExternalUrl(normalizePublicProfileText(config.url))`, both of
 * which reduce to "is there a non-blank string here".
 */
function hasPublishableWebsiteUrl(url?: string | null) {
  return typeof url === 'string' && url.trim().length > 0;
}

export function isPublicProfileBlockSupported(
  type: PublicProfileBlockType,
  mode: 'individual' | 'company',
) {
  return (mode === 'company'
    ? PUBLIC_PROFILE_SUPPORTED_BLOCKS_COMPANY
    : PUBLIC_PROFILE_SUPPORTED_BLOCKS_INDIVIDUAL
  ).includes(type);
}

export function isPublicProfileAuthoredBlock(type: PublicProfileBlockType) {
  return PUBLIC_PROFILE_AUTHORED_BLOCK_TYPES.includes(type);
}

export function isPublicProfileDataBackedBlock(type: PublicProfileBlockType) {
  return PUBLIC_PROFILE_DATA_BACKED_BLOCK_TYPES.includes(type);
}

// Seeded company blocks that "Add a block" never lists (see
// PUBLIC_PROFILE_ADD_BASICS_COMPANY): deleting one takes it off the page for
// good, so the block editor drops the Delete CTA and leaves the visibility
// toggle as the only way to hide it.
export function isPublicProfilePermanentBlock(
  type: PublicProfileBlockType,
  mode: PublicProfileMode,
) {
  return mode === 'company' && PUBLIC_PROFILE_PERMANENT_BLOCKS_COMPANY.includes(type);
}

export function clonePublicProfileBlocks(blocks: PublicProfileBlock[]): PublicProfileBlock[] {
  return blocks.map((block) => ({
    ...block,
    roles: block.roles?.map((role) => ({ ...role })),
  }));
}

export function getDefaultPublicProfileBlocks(mode: 'individual' | 'company') {
  if (mode === 'company') {
    return clonePublicProfileBlocks(DEFAULT_PUBLIC_PROFILE_BLOCKS_COMPANY);
  }
  return clonePublicProfileBlocks(DEFAULT_PUBLIC_PROFILE_BLOCKS_INDIVIDUAL);
}

export function moveArrayItem<T>(list: T[], fromIndex: number, toIndex: number) {
  const next = [...list];
  const [picked] = next.splice(fromIndex, 1);
  if (!picked) {
    return list;
  }
  next.splice(toIndex, 0, picked);
  return next;
}

/**
 * Maps a drag on the editor's *filtered* list (`getEditableProfileBlocks`) back
 * onto indices in the unfiltered `blocks` array, which is what every reorder
 * path takes. Passing the visible indices straight through silently swaps the
 * wrong pair the moment a hidden block sits ahead of the dragged one.
 *
 * Returns `null` when there is nothing to do (same row, or an id that is no
 * longer in `blocks`).
 */
export function resolveVisibleReorderIndices<T extends { id: string }>(
  blocks: T[],
  visibleBlocks: T[],
  fromVisibleIndex: number,
  toVisibleIndex: number,
): { fromIndex: number; toIndex: number } | null {
  const sourceId = visibleBlocks[fromVisibleIndex]?.id;
  const targetId = visibleBlocks[toVisibleIndex]?.id;
  if (!sourceId || !targetId || sourceId === targetId) return null;

  const fromIndex = blocks.findIndex((block) => block.id === sourceId);
  const toIndex = blocks.findIndex((block) => block.id === targetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;

  return { fromIndex, toIndex };
}

export function reorderBlocksByIndices(
  blocks: PublicProfileBlock[],
  fromIndex: number,
  toIndex: number,
) {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= blocks.length || toIndex >= blocks.length) {
    return blocks;
  }
  return moveArrayItem(blocks, fromIndex, toIndex);
}

export function moveBlockByDirection(
  blocks: PublicProfileBlock[],
  blockId: string,
  direction: 'up' | 'down',
) {
  const currentIndex = blocks.findIndex((block) => block.id === blockId);
  if (currentIndex < 0) return blocks;

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= blocks.length) return blocks;

  return moveArrayItem(blocks, currentIndex, targetIndex);
}

export function getPublicProfileTheme(themeId: PublicProfileTheme['id']) {
  return PUBLIC_PROFILE_THEMES.find((theme) => theme.id === themeId) ?? PUBLIC_PROFILE_THEMES[1];
}

export function getPublicProfileQrActionCapabilities(
  platform: string,
): PublicProfileQrActionCapabilities {
  if (platform === 'web') {
    return { canSave: true, canDownload: true };
  }
  return { canSave: true, canDownload: true };
}
